import opentype from 'opentype.js';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import kalamBoldUrl from '@/assets/fonts/Kalam-Bold.ttf';
import kalamLightUrl from '@/assets/fonts/Kalam-Light.ttf';
import kalamUrl from '@/assets/fonts/Kalam-Regular.ttf';

const FONT_STACK = "'Kalam', cursive";
// Must match TextElement (the editor's text renderer) so edit & teach wrap and
// size identically — otherwise the box that fits in edit overflows in teach.
const LINE_HEIGHT = 1.4;

/** Weight → bundled Kalam TTF. Same buckets as `adminChalkAssets.fontUrl` and
 *  the globals.css @font-face weights, so CSS text, edit-mode handwriting and
 *  the shared player all pick the same face. */
function kalamUrlForWeight(weight: number): string {
  if (weight <= 350) return kalamLightUrl;
  if (weight >= 600) return kalamBoldUrl;
  return kalamUrl;
}

/** Parse each bundled Kalam TTF once and share the promise across instances. */
const fontPromises = new Map<string, Promise<opentype.Font>>();
function loadKalam(weight = 400): Promise<opentype.Font> {
  const url = kalamUrlForWeight(weight);
  let p = fontPromises.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((buf) => opentype.parse(buf));
    fontPromises.set(url, p);
  }
  return p;
}

/** A glyph Kalam can draw (outline path) or one it lacks (rendered via fallback font). */
type Piece = { kind: 'path'; d: string } | { kind: 'text'; ch: string; x: number; y: number };

interface Cmd {
  type: string;
  x?: number;
  y?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Re-serialize a glyph outline so each closed contour STARTS at its leftmost
 * point. The shape is identical (we only rotate the starting vertex of the
 * cyclic edge list — no edge is reversed), but because the stroke draw-on and
 * the pencil both follow path order, starting at the left makes the letter
 * write left→right instead of from wherever the font happened to start.
 */
function leftStartPathData(cmds: Cmd[]): string {
  // group into subpaths (each begins with M)
  const subs: Cmd[][] = [];
  let cur: Cmd[] = [];
  for (const c of cmds) {
    if (c.type === 'M') {
      if (cur.length) subs.push(cur);
      cur = [c];
    } else cur.push(c);
  }
  if (cur.length) subs.push(cur);

  const out: string[] = [];
  for (const sub of subs) {
    const m = sub[0];
    const pts: { x: number; y: number }[] = [{ x: m.x ?? 0, y: m.y ?? 0 }];
    const edgeInto: Cmd[] = []; // edgeInto[i] draws from pts[i] to pts[i+1]
    for (let i = 1; i < sub.length; i++) {
      const c = sub[i];
      if (c.type === 'Z') continue;
      pts.push({ x: c.x ?? 0, y: c.y ?? 0 });
      edgeInto.push(c);
    }
    let n = pts.length;
    if (n <= 1) {
      out.push(sub.map(serialize).join(' '));
      continue;
    }
    // drop a final vertex that coincides with the start (explicit close)
    const dup =
      Math.abs(pts[n - 1].x - pts[0].x) < 1e-3 && Math.abs(pts[n - 1].y - pts[0].y) < 1e-3;
    if (dup) n -= 1;

    // edges[i] draws from pts[i] to pts[(i+1)%n]
    const edges: Cmd[] = new Array(n);
    for (let i = 0; i < n; i++) {
      if (i < n - 1) edges[i] = edgeInto[i];
      else edges[i] = dup ? edgeInto[edgeInto.length - 1] : { type: 'L', x: pts[0].x, y: pts[0].y };
    }

    // leftmost vertex
    let k = 0;
    for (let i = 1; i < n; i++) if (pts[i].x < pts[k].x) k = i;

    const parts = [`M ${r2(pts[k].x)} ${r2(pts[k].y)}`];
    for (let j = 0; j < n; j++) {
      const ei = (k + j) % n;
      const dest = pts[(ei + 1) % n];
      const c = edges[ei];
      if (c.type === 'C')
        parts.push(`C ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x2!)} ${r2(c.y2!)} ${r2(dest.x)} ${r2(dest.y)}`);
      else if (c.type === 'Q') parts.push(`Q ${r2(c.x1!)} ${r2(c.y1!)} ${r2(dest.x)} ${r2(dest.y)}`);
      else parts.push(`L ${r2(dest.x)} ${r2(dest.y)}`);
    }
    parts.push('Z');
    out.push(parts.join(' '));
  }
  return out.join(' ');
}

function serialize(c: Cmd): string {
  if (c.type === 'M') return `M ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'L') return `L ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'C') return `C ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x2!)} ${r2(c.y2!)} ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'Q') return `Q ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x!)} ${r2(c.y!)}`;
  return 'Z';
}

interface Built {
  lines: Piece[][]; // pieces grouped by line, left→right
  width: number;
  height: number;
}

/**
 * Lay the text out with the actual Kalam metrics and turn every glyph into an
 * SVG outline path positioned on its baseline. Characters Kalam has no glyph for
 * (arrows, symbols…) fall back to an SVG `<text>` in the CSS font stack.
 */
function build(font: opentype.Font, text: string, size: number, maxWidth: number): Built {
  const scale = size / font.unitsPerEm;
  const ascentPx = font.ascender * scale;
  const lineHeightPx = size * LINE_HEIGHT;
  // Mirror the editor's text box: no horizontal padding, and the first baseline
  // sits a half-leading below the top (CSS line-box behaviour) so edit & teach
  // align. A hair of left pad avoids clipping the leftmost glyph's antialiasing.
  const padX = Math.max(1, size * 0.03);
  const padY = (size * (LINE_HEIGHT - 1)) / 2;

  // Measure with the *browser* (same font string the editor renders with), so
  // wrapping and glyph positions match the CSS layout exactly. opentype is used
  // only for the glyph shape — its advances differ from CSS and caused the
  // edit↔teach width mismatch.
  const canvas = document.createElement('canvas');
  const mctx = canvas.getContext('2d');
  if (mctx) mctx.font = `${size}px ${FONT_STACK}`;
  const measure = (s: string) => (mctx ? mctx.measureText(s).width : s.length * size * 0.5);

  const rawLines: string[] = [];
  for (const para of text.split('\n')) {
    const words = para.split(' ');
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (cur && padX * 2 + measure(test) > maxWidth) {
        rawLines.push(cur);
        cur = w;
      } else cur = test;
    }
    rawLines.push(cur);
  }

  const lines = rawLines.map((line, li) => {
    const baseline = padY + li * lineHeightPx + ascentPx;
    const pieces: Piece[] = [];
    let i = 0; // UTF-16 offset of the current char within the line
    for (const ch of line) {
      // x = where the browser would place this char (prefix width).
      const x = padX + measure(line.slice(0, i));
      if (ch !== ' ') {
        if (font.charToGlyphIndex(ch) !== 0) {
          const cmds = font.getPath(ch, x, baseline, size).commands as Cmd[];
          const d = leftStartPathData(cmds);
          if (d && d.length > 2) pieces.push({ kind: 'path', d });
        } else {
          pieces.push({ kind: 'text', ch, x, y: baseline });
        }
      }
      i += ch.length;
    }
    return pieces;
  });

  return { lines, width: maxWidth, height: padY * 2 + rawLines.length * lineHeightPx };
}

/** A simple pencil, drawn with its writing tip at local (0,0) so we can just
 *  translate the group to the current write point. Scales with font size. */
function PencilNib({ u }: { u: number }) {
  // body half-width and lengths in font-size units
  const hw = u * 0.085;
  const woodTo = u * 0.2;
  const bodyTo = u * 1.05;
  const ferruleTo = u * 1.18;
  const eraserTo = u * 1.34;
  // Body points straight up from the tip at (0,0); the caller rotates the whole
  // group to aim it toward the hand.
  return (
    <g style={{ pointerEvents: 'none' }}>
      <g>
        {/* graphite point */}
        <path
          d={`M0 0 L ${-hw * 0.45} ${-u * 0.08} L ${hw * 0.45} ${-u * 0.08} Z`}
          fill="#2b2b2b"
        />
        {/* wood cone */}
        <path
          d={`M ${-hw * 0.45} ${-u * 0.08} L ${-hw} ${-woodTo} L ${hw} ${-woodTo} L ${hw * 0.45} ${-u * 0.08} Z`}
          fill="#e8c79a"
          stroke="#caa776"
          strokeWidth={u * 0.012}
        />
        {/* painted body */}
        <rect x={-hw} y={-bodyTo} width={hw * 2} height={bodyTo - woodTo} fill="#f5b301" />
        <rect x={-hw} y={-bodyTo} width={hw * 0.66} height={bodyTo - woodTo} fill="#e0a200" />
        {/* metal ferrule */}
        <rect x={-hw} y={-ferruleTo} width={hw * 2} height={ferruleTo - bodyTo} fill="#cfd3d6" />
        {/* eraser */}
        <rect
          x={-hw}
          y={-eraserTo}
          width={hw * 2}
          height={eraserTo - ferruleTo}
          rx={hw * 0.5}
          fill="#e8788a"
        />
      </g>
    </g>
  );
}

/**
 * Handwriting text in Kalam. When `animate` is set (the element was just
 * revealed), the pen "writes" each glyph by laying ink along its skeleton — a
 * thick brush stroke clipped to the glyph silhouette, so even heavy weights draw
 * as a single stroke (not an outer + inner outline). A pencil rides the write
 * point unless `showPen` is off. In edit / scrub it's plain static Kalam.
 */
export function HandwritingText({
  text,
  size,
  color,
  weight = 400,
  animate,
  speed = 0.3,
  showPen = true,
  targetMs,
}: {
  text: string;
  size: number;
  color: string;
  /** Font weight — picks the matching bundled Kalam face (300/400/700). */
  weight?: number;
  /** kept for API compatibility; unused with a real font */
  seed?: string;
  animate: boolean;
  speed?: number;
  showPen?: boolean;
  /** Target total write duration (ms) = the time the line is SPOKEN, so the pen
   *  paces to the narration (slow line → slow pen) instead of a fixed speed. */
  targetMs?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  const [font, setFont] = useState<opentype.Font | null>(null);
  // measureText is only accurate once the CSS Kalam webfont has loaded — re-run
  // the layout when fonts are ready so wrapping matches the editor.
  const [fontsReady, setFontsReady] = useState(false);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  useEffect(() => {
    let on = true;
    loadKalam(weight)
      .then((f) => on && setFont(f))
      .catch(() => {});
    document.fonts?.ready.then(() => on && setFontsReady(true)).catch(() => {});
    return () => {
      on = false;
    };
  }, [weight]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const built = useMemo(
    () => (font && width > 4 && text.trim() ? build(font, text, size, width) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [font, width, text, size, fontsReady],
  );

  // Drive the pen: lay a thick clipped brush along each glyph (single stroke at
  // any weight), fade the fill in behind it, and ride the pencil on the tip.
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || !built || !animate) return;

    // glyph groups + text nodes, in document order
    const items = Array.from(svg.querySelectorAll<SVGElement>('[data-anim]'));

    const inv = 1 / Math.max(0.05, speed * 2); // 2× handwriting speed (user 2026-08-12)
    const brush = Math.max(2.2, size * 0.15);
    const running: Animation[] = [];
    // schedule entries used by the pen tracker — the pencil rides the exact
    // draw point along each glyph's outline (rigid path-follow of the letters).
    type Seg =
      | { kind: 'glyph'; stroke: SVGPathElement; len: number; start: number; dur: number }
      | { kind: 'text'; node: SVGTextElement; start: number; dur: number };
    const segs: Seg[] = [];
    let delay = 0;

    // Pace the write to the teacher's SPEAKING pace: measure the natural total,
    // then time-scale so the whole write fills `targetMs` (the line's spoken
    // duration), clamped 0.4×–8×. No target → fixed speed (factor 1).
    let baseTotal = 0;
    for (const item of items) {
      if (item.tagName.toLowerCase() === 'text') { baseTotal += 150 * inv + 18 * inv; continue; }
      const st0 = (item as SVGGElement).querySelector<SVGPathElement>('path[data-stroke]');
      const len0 = st0?.getTotalLength() || 1;
      baseTotal += Math.min(1100, Math.max(70, len0 * 1.4 * inv)) + 18 * inv;
    }
    const factor = targetMs && baseTotal > 0 ? Math.max(0.4, Math.min(8, targetMs / baseTotal)) : 1;
    const gap = 18 * inv * factor;

    for (const item of items) {
      if (item.tagName.toLowerCase() === 'text') {
        const node = item as SVGTextElement;
        const dur = 150 * inv * factor;
        node.style.fillOpacity = '0';
        running.push(
          node.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], {
            duration: dur,
            delay,
            fill: 'forwards',
            easing: 'ease-out',
          }),
        );
        segs.push({ kind: 'text', node, start: delay, dur });
        delay += dur + gap;
        continue;
      }
      const g = item as SVGGElement;
      const stroke = g.querySelector<SVGPathElement>('path[data-stroke]')!;
      const fillP = g.querySelector<SVGPathElement>('path[data-fill]')!;
      const len = stroke.getTotalLength() || 1;
      const dur = Math.min(1100, Math.max(70, len * 1.4 * inv)) * factor;
      // thick brush clipped to the glyph silhouette → fills the stem in one pass
      // (single stroke at any weight) while the pen traces the outline path.
      stroke.style.stroke = color;
      stroke.style.strokeWidth = String(brush);
      stroke.style.strokeDasharray = String(len);
      stroke.style.strokeDashoffset = String(len);
      fillP.style.fillOpacity = '0';
      running.push(
        stroke.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], {
          duration: dur,
          delay,
          fill: 'forwards',
          easing: 'linear',
        }),
        // crisp fill settles in behind the brush
        fillP.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], {
          duration: Math.min(180, dur * 0.5),
          delay: delay + dur * 0.55,
          fill: 'forwards',
          easing: 'ease-out',
        }),
      );
      segs.push({ kind: 'glyph', stroke, len, start: delay, dur });
      delay += dur + gap;
    }

    // Pencil follows the active write point.
    const nib = showPen ? svg.querySelector<SVGGElement>('g[data-nib]') : null;
    let raf = 0;
    if (nib) {
      const total = delay;
      const t0 = performance.now();
      // Smoothed "hand" position (up-right of the tip). We ease the hand, not
      // the tip — so the tip stays exactly on the path while the pencil pivots.
      let hx = NaN;
      let hy = NaN;
      // A little occasional extra tilt for variance (not a constant swing).
      let rot = 0;
      let rotTarget = 0;
      let nextRot = 500 + Math.random() * 800;
      const tick = () => {
        const t = performance.now() - t0;
        if (t >= total) {
          nib.style.opacity = '0';
          return;
        }
        const seg = segs.find((s) => t >= s.start && t <= s.start + s.dur);
        if (!seg) {
          // between glyphs — keep the pen where the last stroke ended
          raf = requestAnimationFrame(tick);
          return;
        }
        let px = 0;
        let py = 0;
        if (seg.kind === 'glyph') {
          // rigid path-follow: pen sits exactly on the outline draw point.
          const frac = Math.max(0, Math.min(1, (t - seg.start) / seg.dur));
          const pt = seg.stroke.getPointAtLength(seg.len * frac);
          px = pt.x;
          py = pt.y;
        } else {
          px = seg.node.x.baseVal.getItem(0)?.value ?? 0;
          py = seg.node.y.baseVal.getItem(0)?.value ?? 0;
        }
        // The hand rides up-right of the tip; smoothing the HAND (not the tip)
        // absorbs the outline jitter — the tip stays exactly on the path and the
        // pencil pivots. Moving the whole pencil with the tip is what jittered.
        const L = size * 1.05;
        const ax = px + L * 0.5; // right of the tip
        const ay = py - L * 0.87; // and above it
        if (Number.isNaN(hx)) {
          hx = ax;
          hy = ay;
        } else {
          hx += (ax - hx) * 0.15;
          hy += (ay - hy) * 0.15;
        }
        const pivot = (Math.atan2(hy - py, hx - px) * 180) / Math.PI + 90;
        // occasional small extra tilt (variance, not a constant pendulum)
        if (t >= nextRot) {
          if (Math.random() < 0.6) rotTarget = (Math.random() * 2 - 1) * 8;
          nextRot = t + 600 + Math.random() * 1500;
        }
        rot += (rotTarget - rot) * 0.1;
        nib.style.opacity = '1';
        nib.setAttribute('transform', `translate(${px} ${py}) rotate(${pivot + rot})`);
        raf = requestAnimationFrame(tick);
      };
      nib.style.opacity = '0';
      raf = requestAnimationFrame(tick);
    }

    return () => {
      running.forEach((a) => a.cancel());
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, animate, speed, size, color, showPen, targetMs]);

  // Static path: plain Kalam HTML (edit / scrub) — crisp & identical letterforms.
  if (!animate) {
    return (
      <div
        ref={wrapRef}
        className="h-full w-full"
        style={{
          fontFamily: FONT_STACK,
          fontSize: size,
          lineHeight: LINE_HEIGHT,
          color,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontWeight: 400,
        }}
      >
        {text}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="h-full w-full" style={{ position: 'relative' }}>
      {built ? (
        <svg
          ref={svgRef}
          width={built.width}
          height={built.height}
          viewBox={`0 0 ${built.width} ${built.height}`}
          style={{ display: 'block', overflow: 'visible' }}
        >
          <defs>
            <filter id={`${uid}-nib-shadow`} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow
                dx="0"
                dy={size * 0.03}
                stdDeviation={size * 0.03}
                floodColor="rgba(0,0,0,0.35)"
              />
            </filter>
            {(() => {
              let ci = 0;
              return built.lines.flatMap((line) =>
                line
                  .filter((p): p is Extract<Piece, { kind: 'path' }> => p.kind === 'path')
                  .map((g) => {
                    const id = `${uid}-clip-${ci++}`;
                    return (
                      <clipPath key={id} id={id}>
                        <path d={g.d} />
                      </clipPath>
                    );
                  }),
              );
            })()}
          </defs>
          {(() => {
            let ci = 0;
            return built.lines.flatMap((line, li) =>
              line.map((g, idx) => {
                if (g.kind === 'text') {
                  return (
                    <text
                      key={`${li}-${idx}`}
                      data-anim=""
                      data-t=""
                      x={g.x}
                      y={g.y}
                      fill={color}
                      fontFamily={FONT_STACK}
                      fontSize={size}
                      style={{ fillOpacity: 0 }}
                    >
                      {g.ch}
                    </text>
                  );
                }
                const clipId = `${uid}-clip-${ci++}`;
                return (
                  <g key={`${li}-${idx}`} data-anim="" data-glyph="">
                    {/* thick brush, clipped to the glyph → single stroke at any weight */}
                    <path
                      data-stroke=""
                      d={g.d}
                      fill="none"
                      stroke="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      clipPath={`url(#${clipId})`}
                    />
                    {/* crisp fill settles in behind the brush */}
                    <path data-fill="" d={g.d} fill={color} style={{ fillOpacity: 0 }} />
                  </g>
                );
              }),
            );
          })()}
          {showPen && (
            <g data-nib="" style={{ opacity: 0 }}>
              <g filter={`url(#${uid}-nib-shadow)`}>
                <PencilNib u={size * 0.82} />
              </g>
            </g>
          )}
        </svg>
      ) : (
        // Pre-font fallback so the reveal is never blank.
        <div
          style={{ fontFamily: FONT_STACK, fontSize: size, lineHeight: LINE_HEIGHT, color }}
        >
          {text}
        </div>
      )}
    </div>
  );
}
